import { ENTRY_WINDOW_PHASES, type EntryWindow } from "@seagames/contracts";
import { type ColumnDef, DataTable, ListHeader, ListToolbar, PrimaryCell, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { ConfirmDeleteModal } from "../../components/common/confirm-delete-modal";
import { EditDeleteActions } from "../../components/common/edit-delete-actions";
import { StatusGuide } from "../../components/common/status-guide";
import { Timestamp } from "../../components/common/list-columns";
import { useEntriesGuide } from "../../components/entries/parts";
import { type EntryWindowFormTarget, EntryWindowForm } from "../../components/entries/window-form";
import { WindowStatusPill, windowState } from "../../components/registration-windows/window-status";
import { gamesWithDatesOptions, liveGames } from "../../lib/accreditation/register-options";
import { deleteEntryWindow, entryWindowsListOptions, invalidateEntryWindows } from "../../lib/api/entries";
import { listSearch } from "../../lib/list-page/list-search";
import { useConfirmDelete } from "../../lib/list-page/use-confirm-delete";
import { useListPage } from "../../lib/list-page/use-list-page";
import { responsibleOrganizationOptionsOptions } from "../../lib/responsible-organizations/queries";

/** The list has one fixed order (games, phase, organisation, opens) — no sort param on the API. */
const SORT_KEYS = ["opens_at"] as const;

export const Route = createFileRoute("/_app/settings/entry-windows")({
	validateSearch: listSearch(SORT_KEYS, {
		games_id: z.string().uuid(),
		phase: z.enum(ENTRY_WINDOW_PHASES),
		organization_id: z.string().uuid(),
	}),
	component: EntryWindowsPage,
});

interface WindowFilters extends Record<string, unknown> {
	games: string;
	phase: string;
	organisation: string;
}
const EMPTY_FILTERS: WindowFilters = { games: "", phase: "", organisation: "" };

type ColumnKey = "phase" | "games" | "organisation" | "opens_at" | "closes_at" | "status";

/**
 * Game Settings › Entry windows (M7b): when NOCs may change and submit their
 * Expression of interest / Entry by number, for one games, every NOC or one.
 * Outside every window scoped logins cannot write or submit (409
 * ENTRY_WINDOW_CLOSED); admins never are bound. ADMIN reads, SUPER_ADMIN
 * writes. The Registration windows page, for the entry phases.
 */
function EntryWindowsPage() {
	const { t } = useTranslation("entries");
	const { formatNumber } = useUiStrings();
	const search = Route.useSearch();
	const { session } = Route.useRouteContext();
	const isSuper = session.active_role === "SUPER_ADMIN";
	const [form, setForm] = useState<EntryWindowFormTarget | null>(null);
	const games = useQuery(gamesWithDatesOptions());
	const gamesName = (id: string, code: string) => games.data?.find((g) => g.games_id === id)?.name ?? code;
	const orgs = useQuery(responsibleOrganizationOptionsOptions());

	const page = useListPage({
		search,
		navigate: Route.useNavigate(),
		listOptions: (q) =>
			entryWindowsListOptions({
				page: q.page,
				page_size: q.page_size,
				games_id: search.games_id,
				phase: search.phase,
				organization_id: search.organization_id,
			}),
		columnsKey: "entry-windows",
		filters: {
			empty: EMPTY_FILTERS,
			fromUrl: (s): WindowFilters => ({ games: s.games_id ?? "", phase: s.phase ?? "", organisation: s.organization_id ?? "" }),
			toUrl: (f) => ({
				games_id: f.games || undefined,
				phase: (f.phase || undefined) as (typeof ENTRY_WINDOW_PHASES)[number] | undefined,
				organization_id: f.organisation || undefined,
			}),
		},
		summary: { range: (v) => t("windows.pagination.summary", v), empty: t("windows.pagination.empty") },
	});
	const total = page.list.data?.total;
	const now = Date.now();
	const guide = useEntriesGuide();

	const del = useConfirmDelete<EntryWindow>({
		remove: (r) => deleteEntryWindow(r.entry_window_id, `Deleted entry window ${r.games.code} ${r.phase}`),
		invalidate: invalidateEntryWindows,
		deleted: () => t("windows.toast.deleted"),
	});

	const columns: ColumnDef<EntryWindow, ColumnKey>[] = [
		{ key: "phase", header: t("windows.table.phase"), lockVisible: true, minWidth: "12rem", cell: (r) => <PrimaryCell>{t(`phase.${r.phase}`)}</PrimaryCell> },
		{ key: "games", header: t("windows.table.games"), minWidth: "8rem", cell: (r) => <span className="font-medium" title={r.games.code}>{gamesName(r.games.id, r.games.code)}</span> },
		{
			key: "organisation",
			header: t("windows.table.organisation"),
			minWidth: "10rem",
			cell: (r) => (r.organization ? r.organization.name : <span className="text-base-content/55">{t("windows.table.allOrganisations")}</span>),
		},
		{ key: "opens_at", header: t("windows.table.opens"), minWidth: "9rem", cell: (r) => <Timestamp iso={r.opens_at} /> },
		{ key: "closes_at", header: t("windows.table.closes"), minWidth: "9rem", cell: (r) => <Timestamp iso={r.closes_at} /> },
		{ key: "status", header: t("windows.table.status"), minWidth: "7rem", cell: (r) => <WindowStatusPill state={windowState(r, now)} /> },
	];

	return (
		<div className="flex min-w-0 flex-col gap-[1.125rem]">
			<ListHeader
				title={t("windows.title")}
				count={total === undefined ? undefined : t("windows.count", { count: total, n: formatNumber(total) })}
				description={t("windows.intro")}
				actionLabel={t("windows.add")}
				onAction={isSuper ? () => setForm({ mode: "create", gamesId: search.games_id }) : undefined}
			/>

			<ListToolbar
				chips={[
					{
						kind: "options",
						key: "games",
						label: t("windows.filters.games"),
						options: liveGames(games.data, search.games_id).map((g) => ({ value: g.games_id, label: g.name })),
						value: search.games_id ? [search.games_id] : [],
						onChange: (v) => page.patch({ games_id: v[0] ?? undefined }),
					},
					{
						kind: "options",
						key: "phase",
						label: t("windows.filters.phase"),
						options: ENTRY_WINDOW_PHASES.map((p) => ({ value: p, label: t(`phase.${p}`) })),
						value: search.phase ? [search.phase] : [],
						onChange: (v) => page.patch({ phase: (v[0] as (typeof ENTRY_WINDOW_PHASES)[number] | undefined) ?? undefined }),
					},
					{
						kind: "menu",
						key: "organisation",
						label: t("windows.filters.organisation"),
						options: (orgs.data ?? []).map((o) => ({ value: o.responsible_organization_id, label: o.name })),
						value: search.organization_id ?? null,
						onChange: (v) => page.patch({ organization_id: v ?? undefined }),
						searchPlaceholder: t("windows.filters.search"),
					},
				]}
				end={<StatusGuide title={guide.title} groups={guide.groups.filter((g) => g.key === "window")} />}
				onClearAll={page.clearFilters}
			/>

			<DataTable<EntryWindow, ColumnKey>
				{...page.table}
				variant="list"
				columns={columns}
				rowKey={(r) => r.entry_window_id}
				empty={page.emptyState({
					filtered: { title: t("windows.empty.filteredTitle"), description: t("windows.empty.filteredHint") },
					none: { title: t("windows.empty.noneTitle"), description: t("windows.empty.noneHint") },
					clearLabel: t("windows.empty.clear"),
				})}
				rowActions={
					isSuper
						? (r) => (
								<EditDeleteActions
									editLabel={t("windows.table.editLabel", { phase: t(`phase.${r.phase}`) })}
									onEdit={() => setForm({ mode: "edit", window: r })}
									deleteLabel={t("windows.table.deleteLabel", { phase: t(`phase.${r.phase}`) })}
									onDelete={() => del.ask(r)}
									variant="list"
								/>
							)
						: undefined
				}
			/>

			{isSuper && <EntryWindowForm target={form} onClose={() => setForm(null)} />}
			{isSuper && (
				<ConfirmDeleteModal
					{...del.modal}
					title={t("windows.delete.title")}
					description={t("windows.delete.description", { phase: del.row ? t(`phase.${del.row.phase}`) : "" })}
					confirmLabel={t("windows.delete.confirm")}
					errorPrefix={t("windows.delete.failed")}
				/>
			)}
		</div>
	);
}
